import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { URL_CAPITAL_PLAY_REFORMA_DETALLE } from '@constants/reforma-pensiones.constants';
import { IContenidoReformaPensionesEntry } from '@interfaces/capital-play-content.interface';
import { CapitalPlayContentService } from '../../services/capital-play-content.service';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

@Component({
  selector: 'app-reforma-previsional-content',
  templateUrl: './reforma-previsional-content.component.html',
  styleUrl: './reforma-previsional-content.component.scss',
  standalone: false
})
export class ReformaPrevisionalContentComponent implements OnInit, OnDestroy {

  @ViewChild('track', { static: false }) trackRef: ElementRef<HTMLDivElement>;

  entries: IContenidoReformaPensionesEntry[] = [];
  loading: boolean = true;
  hasError: boolean = false;
  itemsPerView: number = 3;
  currentPage: number = 0;
  isDragging: boolean = false;

  private readonly AUTOPLAY_DELAY_MS = 5000;
  private autoplayTimer: ReturnType<typeof setInterval> | null = null;

  private dragStartX: number = 0;
  private dragScrollLeft: number = 0;
  private dragMoved: boolean = false;
  private pointerCaptured: boolean = false;

  constructor(private readonly capitalPlayContentService: CapitalPlayContentService) { }

  ngOnInit(): void {
    this.setItemsPerView();
    void this.loadEntries();
  }

  ngOnDestroy(): void {
    this.stopAutoplay();
  }

  get totalPages(): number {
    return this.itemsPerView > 0 ? Math.ceil(this.entries.length / this.itemsPerView) : 0;
  }

  get pages(): number[] {
    return Array.from({ length: this.totalPages }, (_, index) => index);
  }

  formatDate(fecha: string): string {
    const date = new Date(fecha);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return `${MESES[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  }

  goToEntry(entry: IContenidoReformaPensionesEntry): void {
    if (this.dragMoved) {
      return;
    }
    const videoUrl = entry.fields?.['Link video'];
    const url = videoUrl ? videoUrl : `${URL_CAPITAL_PLAY_REFORMA_DETALLE}/${entry.meta.slug}`;
    window.open(url, '_blank');
  }

  onPointerDown(event: PointerEvent): void {
    const track = this.trackRef?.nativeElement;
    if (!track || event.pointerType !== 'mouse') {
      return;
    }
    this.isDragging = true;
    this.dragMoved = false;
    this.pointerCaptured = false;
    this.dragStartX = event.clientX;
    this.dragScrollLeft = track.scrollLeft;
    this.pauseAutoplay();
  }

  onPointerMove(event: PointerEvent): void {
    const track = this.trackRef?.nativeElement;
    if (!track || !this.isDragging) {
      return;
    }
    const delta = event.clientX - this.dragStartX;
    if (!this.dragMoved && Math.abs(delta) > 5) {
      this.dragMoved = true;
      track.setPointerCapture(event.pointerId);
      this.pointerCaptured = true;
    }
    if (this.dragMoved) {
      track.scrollLeft = this.dragScrollLeft - delta;
    }
  }

  onPointerUp(event: PointerEvent): void {
    const track = this.trackRef?.nativeElement;
    if (!this.isDragging) {
      return;
    }
    this.isDragging = false;
    if (this.pointerCaptured && track?.hasPointerCapture(event.pointerId)) {
      track.releasePointerCapture(event.pointerId);
    }
    this.pointerCaptured = false;
    this.resumeAutoplay();
  }

  scrollToPage(page: number): void {
    const track = this.trackRef?.nativeElement;
    if (!track) {
      return;
    }
    track.scrollTo({ left: page * track.clientWidth, behavior: 'smooth' });
    this.resumeAutoplay();
  }

  onScroll(): void {
    const track = this.trackRef?.nativeElement;
    if (!track || !track.clientWidth) {
      return;
    }
    this.currentPage = Math.round(track.scrollLeft / track.clientWidth);
  }

  pauseAutoplay(): void {
    this.stopAutoplay();
  }

  resumeAutoplay(): void {
    this.startAutoplay();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.setItemsPerView();
  }

  private async loadEntries(): Promise<void> {
    try {
      this.entries = await this.capitalPlayContentService.getReformaEntries();
      this.hasError = false;
      this.startAutoplay();
    } catch (error) {
      console.error('Error al obtener el contenido de Reforma Previsional', error);
      this.hasError = true;
    } finally {
      this.loading = false;
    }
  }

  private setItemsPerView(): void {
    const width = window.innerWidth;
    if (width < 480) {
      this.itemsPerView = 1.15;
    } else if (width < 700) {
      this.itemsPerView = 1.5;
    } else if (width < 992) {
      this.itemsPerView = 2.2;
    } else if (width < 1200) {
      this.itemsPerView = 2.5;
    } else {
      this.itemsPerView = 3;
    }
  }

  private startAutoplay(): void {
    this.stopAutoplay();
    if (this.totalPages <= 1) {
      return;
    }
    this.autoplayTimer = setInterval(() => this.advancePage(), this.AUTOPLAY_DELAY_MS);
  }

  private stopAutoplay(): void {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  private advancePage(): void {
    const track = this.trackRef?.nativeElement;
    if (!track || this.totalPages === 0) {
      return;
    }
    const nextPage = (this.currentPage + 1) % this.totalPages;
    track.scrollTo({ left: nextPage * track.clientWidth, behavior: 'smooth' });
  }
}